import { useCallback, useLayoutEffect, useRef } from 'react'
import { LoadingContext } from '@components/Context/LoadingContext'
import React from 'react'

const inProgressSuffix = 'InProgress'

export type UseLoadingContextOptions<TArgs extends unknown[] = []> = {
  /**
   * Async method needing to await processing.
   **/
  asyncFunction: (...args: TArgs) => Promise<void>
  /**
   * Unique Id of the operationId + component name being awaited.
   **/
  loadingId: string
  /**
   * Optional callback method to perform any actions after the async function is complete
   **/
  callback?: () => void
}

export type UseLoadingContext<TArgs extends unknown[] = []> = {
  /**
   * Indicates whether the fetch operation is in progress.
   * this indicator is directly related to the loadingId provided in the hook options
   **/
  isLoading: boolean
  /**
   * Initiates the fetch process and adds the loading ID to the Loading context.
   **/
  triggerFetch: (...args: TArgs) => void
  /**
   * Exposes the functions of the loadingContext avoiding having to reimport
   * the context when this hook has already been imported
   * */
  loadingIds: Set<string>
  addLoadingIds: (state: string[]) => void
  removeLoadingIds: (state: string[]) => void
}

/**
 *    How to use:
 *
 *    1. Import the hook:
 *    import useLoadingContext from 'path/to/useLoadingContext';
 *
 *    2. Define your async function:
 *    const fetchData = async () => {
 *       Your async operation, e.g., fetching data from an API.
 *    };
 *
 *    3. Define your loadingId:
 *     const availablesLoadingIds = useLoadingIds()
 *
 *    4. Call the hook with the necessary options:
 *    const { isLoading, triggerFetch } = useLoadingContext({
 *      asyncFunction: fetchData,
 *      loadingId,
 *      callback: () => {
 *        console.log('Fetch operation completed');
 *      },
 *    });
 *
 *    5. Trigger the fetch operation:
 *    triggerFetch();
 *
 *    6. Use the `isLoading` boolean to display a loading indicator:
 *    {isLoading && <LoadingSpinner />}
 *
 */
const useLoadingContext = <TArgs extends unknown[] = []>({
  asyncFunction,
  callback,
  loadingId,
}: UseLoadingContextOptions<TArgs>): UseLoadingContext<TArgs> => {
  const { loadingIds, addLoadingIds, removeLoadingIds } =
    React.useContext(LoadingContext)

  const inProgressId = `${loadingId}${inProgressSuffix}`
  const isRequestingRef = useRef(false)

  // Use refs to avoid recreating triggerFetch on every render when these change
  const asyncFunctionRef = useRef(asyncFunction)
  const callbackRef = useRef(callback)

  /**
   * We use layout effect instead of useEffect to ensure that the refs are updated
   * before the next render.
   * This is important because the triggerFetch function closes over these refs.
   */
  useLayoutEffect(() => {
    asyncFunctionRef.current = asyncFunction
    callbackRef.current = callback
  }, [asyncFunction, callback])

  const triggerFetch = useCallback(
    (...args: TArgs) => {
      // Synchronous lock to prevent rapid double-clicks (race conditions)
      if (isRequestingRef.current) {
        return
      }

      isRequestingRef.current = true

      const execute = async () => {
        addLoadingIds([loadingId, inProgressId])
        try {
          await asyncFunctionRef.current(...args)
        } catch (err) {
          // Error handling for the requests should not be handled here.
        } finally {
          isRequestingRef.current = false
          // Order of removal should be important. We want to show we're done loading and then we're finished processing.
          removeLoadingIds([loadingId, inProgressId])
          callbackRef.current?.()
        }
      }

      execute()
    },
    [addLoadingIds, removeLoadingIds, loadingId, inProgressId]
  )

  const isLoading = loadingIds?.has?.(loadingId) || false

  return {
    isLoading,
    triggerFetch,
    loadingIds,
    addLoadingIds,
    removeLoadingIds,
  }
}

export default useLoadingContext
